import { FieldSpecs, money, quoted } from '@api/ActivityLogs/DescribeChanges';
import { ActivityOrigin, buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import { Customer, PointTransaction, PointTransactionType } from '@api/Customers/Customer';
import { Reward } from '@api/Rewards/Reward';
import { cashText } from '@api/ActivityLogs/CashActivity';
import { toDateSafe } from '@utils/Date';

/**
 * Qué se audita de CLIENTES Y PUNTOS (M4 del `PLAN-BITACORA-ACTIVITY-LOGS.md`).
 *
 * El plan lo llama el módulo **más delicado**, y medido lo es: hasta el
 * 2026-09-11 `CustomerServ` **no tenía ni un `addActivityLog`**. Tres cosas se
 * podían hacer sin dejar rastro, y las tres mueven dinero de un cliente:
 *
 * 1. **Cambiar la tasa de puntos** (`reward`, un singleton) — gobierna cuánto
 *    gana **toda la cadena** en cada compra.
 * 2. **Borrar un movimiento de puntos** — y esto es peor que "no registrar":
 *    **destruye la evidencia que ya existía**, porque un ajuste manual guarda
 *    dentro `createdBy`. Borrarlo se llevaba por delante al único testigo.
 * 3. **Editar los datos del cliente** (nombre, correo).
 *
 * ⚠️ **Los puntos que se ganan y se canjean NO se registran aquí, a propósito**
 * (D6): cada movimiento ya queda en el historial de la cartera del cliente, con
 * su fecha, su origen y su descripción — es una pantalla que el admin abre. Son
 * además miles al día. La bitácora registra **lo que NO deja rastro en otro
 * lado**: que alguien lo borre, o que cambie la regla con la que se calculan.
 */

/** Lo que un admin puede editar del cliente desde el panel. */
export const CUSTOMER_FIELDS: FieldSpecs<Customer> = {
    email: { label: 'el correo', format: quoted },
    // ⚠️ Hoy ningún camino del admin cambia el teléfono (`saveCustomer` solo lo
    // fija al crear), pero va en la lista igual: **el teléfono ES la identidad**
    // del cliente (OTP, búsqueda del POS, QR del pase), así que el día que algo
    // lo mueva, ese cambio tiene que quedar registrado sin que nadie se acuerde
    // de agregarlo aquí.
    phone: { label: 'el teléfono', format: quoted }
};

/** El perfil va anidado, así que sus campos se describen aparte. */
export const CUSTOMER_PROFILE_FIELDS = {
    name: { label: 'el nombre', format: quoted },
    firstName: { label: 'el nombre', format: quoted },
    lastName: { label: 'el apellido', format: quoted }
};

/**
 * La tasa de puntos: el documento más pequeño del sistema y el que más dinero
 * mueve. `points.cost` es cuántos pesos cuesta ganar `rewardPoints`; `reward` es
 * el canje. Un cero en `points.cost` daba **Infinity** en la cartera antes del
 * guard de `getEarnedPoints` — por eso cambiarlo tiene que verse.
 */
export const REWARD_FIELDS: FieldSpecs<Record<string, unknown>> = {
    'points.cost': { label: 'los pesos que cuesta ganar puntos', format: money },
    'points.rewardPoints': { label: 'los puntos que se ganan' },
    'points.name': { label: 'el nombre del programa', format: quoted },
    'reward.costPoints': { label: 'los puntos que cuesta canjear' },
    'reward.value': { label: 'el valor del canje', format: money },
    'newUser.status': {
        label: 'el bono de bienvenida',
        verbs: { on: 'activó el bono de bienvenida', off: 'desactivó el bono de bienvenida' }
    },
    'newUser.points': { label: 'los puntos de bienvenida' }
};

/** Aplana `reward` a las llaves con punto que describen los specs de arriba. */
export const flattenReward = (reward?: Reward): Record<string, unknown> => ({
    'points.cost': reward?.points?.cost,
    'points.rewardPoints': reward?.points?.rewardPoints,
    'points.name': reward?.points?.name,
    'reward.costPoints': reward?.reward?.costPoints,
    'reward.value': reward?.reward?.value,
    'newUser.status': reward?.newUser?.status,
    'newUser.points': reward?.newUser?.points
});

const TIPO_DE_MOVIMIENTO: Record<string, string> = {
    [PointTransactionType.Earned]: 'ganados',
    [PointTransactionType.Spent]: 'usados',
    [PointTransactionType.Initial]: 'de bienvenida'
};

/**
 * El renglón de un movimiento de puntos BORRADO.
 *
 * ⚠️ Lleva TODO dentro del texto —puntos, tipo, fecha, descripción y quién lo
 * había otorgado— porque el movimiento desaparece del arreglo y **con él su
 * `createdBy`**: es el único caso del sistema donde borrar destruye evidencia en
 * vez de solo quitar un dato. Si esta frase se queda corta, no hay dónde ir a
 * buscar lo que faltaba.
 */
export const describeDeletedPointTransaction = (transaction: PointTransaction): string => {
    const tipo = TIPO_DE_MOVIMIENTO[transaction.type] || transaction.type;
    const fecha = toDateSafe(transaction.date as never);
    const cuando = fecha ? ` del ${fecha.toLocaleDateString('es-MX')}` : '';
    const descripcion = (transaction.description || '').trim();
    const detalle = descripcion ? ` ("${descripcion}")` : '';
    const autor = getTransactionAuthor(transaction);
    const porQuien = autor ? `, que había otorgado ${autor}` : '';
    return `borró un movimiento de ${transaction.points} puntos ${tipo}${cuando}${detalle}${porQuien}`;
};

/** El nombre de quien creó un movimiento, tolerando las formas en que se guardó. */
export const getTransactionAuthor = (transaction: PointTransaction): string => {
    const createdBy = (transaction as { createdBy?: unknown }).createdBy as
        | { profile?: { name?: string }; name?: string }
        | string
        | undefined;
    if (!createdBy) return '';
    if (typeof createdBy === 'string') return '';
    return (createdBy.profile?.name || createdBy.name || '').trim();
};

/** Ajuste manual del saldo desde el panel: cuántos puntos y en qué quedó. */
export const describeManualPoints = (points: number, newBalance: number): string =>
    points >= 0
        ? `otorgó ${points} puntos a mano (saldo: ${newBalance})`
        : `quitó ${Math.abs(points)} puntos a mano (saldo: ${newBalance})`;

/**
 * El primer renglón del cliente, DERIVADO de lo que ya sabe de sí mismo (D8).
 *
 * Es el mejor caso de todo el plan: `createdAt` está en **14,957 de 14,959
 * clientes**. Sin autor a propósito —casi siempre se registra el cliente solo—,
 * y con la sucursal cuando se conoce. **Cero lecturas y cero escrituras**: se
 * arma con el objeto que la pantalla ya tiene en memoria.
 */
export const buildCustomerOrigin = (customer?: Customer, branchOfficeName?: string): ActivityOrigin[] => {
    const origin = buildActivityOrigin(customer as never, {
        noun: 'Cliente registrado',
        verb: 'registró al cliente',
        detail: branchOfficeName ? `desde ${branchOfficeName}` : undefined
    });
    return origin ? [origin] : [];
};

/** Para que el CLAUDE.md y las pruebas hablen del mismo formato de dinero. */
export { cashText };
